import { DateTime } from "luxon";

/** Mois abrégés des sélecteurs de mois (calendriers, date-pickers), dans l'ordre de janvier à décembre. */
const MONTHS_SHORT = [
	"Jan.", "Fév.", "Mar.", "Avr.", "Mai", "Juin",
	"Juil.", "Août", "Sep.", "Oct.", "Nov.", "Déc.",
];

/** Mois en toutes lettres, même ordre — libellés des sélecteurs et en-têtes de regroupement. */
const MONTHS = [
	"Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
	"Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function getShortFutureRelativeDate(date: number | string | undefined, now: DateTime, limitInHours = 2): string
{
	if (! now)
	{
		return "";
	}

	const startAt = DateTime.fromSeconds(Number(date));

	if (! startAt)
	{
		return "";
	}

	// Calculer la différence entre la date de début et l'heure actuelle (futur uniquement)
	const diff = startAt.diff(now, [ "hours", "minutes", "seconds" ]);

	// Si la différence est négative (startAt est dans le passé), on ne retourne rien
	if (diff.valueOf() <= 0)
	{
		return "";
	}

	// Obtenir le nombre d'heures et de minutes restant
	const hours = Math.floor(diff.hours);
	const minutes = Math.floor(diff.minutes);
	const seconds = Math.floor(diff.seconds);

	if (hours < limitInHours)
	{
		if (hours > 0)
		{
			return `Dans ${ hours }h ${ minutes }m ${ seconds }s`;
		}
		else
		{
			return `Dans ${ minutes }m ${ seconds }s`;
		}
	}

	return "";
}

function isNow(startAt: number | undefined, endAt: number | undefined, now: number): boolean
{
	// Vérifier que les deux dates sont définies
	if (! startAt || ! endAt)
	{
		return false;
	}

	// Vérifier si l'heure actuelle est entre startAt et endAt
	//const now = DateTime.now();
	return now >= Number(startAt) && now <= Number(endAt);
}

function delay(ms: number)
{
	return new Promise(resolve => setTimeout(resolve, ms));
}

function parseToDateTime(date: Date | DateTime | string | undefined | null): DateTime | undefined
{
	if (date instanceof DateTime)
	{
		return date;
	}

	if (date instanceof Date)
	{
		return DateTime.fromJSDate(date);
	}

	if (typeof date === "string")
	{
		// 1. ISO
		const iso = DateTime.fromISO(date);
		if (iso.isValid)
		{
			return iso;
		}

		// 2. Format français avec heure : "Mer. 12 Nov. 2025 à 11:00"
		const fr = DateTime.fromFormat(date, "EEE. dd MMM. yyyy 'à' HH:mm", { locale: "fr" });
		if (fr.isValid)
		{
			return fr;
		}

		// 2b. Format français date seule : "Ven. 3 Avr. 2026" (jour sans zéro initial)
		const frDate = DateTime.fromFormat(date, "EEE. d MMM. yyyy", { locale: "fr" });
		if (frDate.isValid)
		{
			return frDate;
		}

		// 3. Format court : "Mer. 12/11 - 12:00"
		const currentYear = DateTime.now().year;
		const frShort = DateTime.fromFormat(
			`${ date }/${ currentYear }`,
			"EEE. dd/LL '-' HH:mm/yyyy",
			{ locale: "fr" }
		);
		if (frShort.isValid)
		{
			return frShort;
		}


		// 4. Tentative générique
		const parsed = DateTime.fromJSDate(new Date(date));
		if (parsed.isValid)
		{
			return parsed;
		}
	}

	return undefined;
}

function calculateAge(birthdate: string | undefined | null): number | null
{
	if (!birthdate)
	{
		return null;
	}

	const birthdateDateTime = DateTime.fromISO(birthdate);
	if (!birthdateDateTime.isValid)
	{
		return null;
	}

	return Math.floor(DateTime.now().diff(birthdateDateTime, "years").years);
}

/**
 * À partir d'un weekIso (`"2026-W26"`), retourne les libellés affichés dans les en-têtes
 * /workouts (web + ionic) : titre principal ("Cette semaine" si on est dans la semaine
 * courante, sinon "Semaine du dd MMMM") + sous-titre plage de dates ("lun. 22/06 - dim. 28/06").
 * `shortTitle` abrège en "Sem. du dd MMMM" pour l'en-tête ionic, plus étroit.
 */
function getWeekLabels(weekIso: string): { title: string, shortTitle: string, subtitle: string, isCurrentWeek: boolean }
{
	const weekStart = DateTime.fromISO(weekIso).setLocale("fr").startOf("week");
	const isCurrentWeek = weekStart.hasSame(DateTime.now().startOf("week"), "week");
	return {
		isCurrentWeek,
		title: isCurrentWeek ? "Cette semaine" : `Semaine du ${ weekStart.toFormat("dd MMMM") }`,
		shortTitle: isCurrentWeek ? "Cette semaine" : `Sem. du ${ weekStart.toFormat("dd MMMM") }`,
		subtitle: `${ weekStart.toFormat("ccc dd/MM") } - ${ weekStart.endOf("week").toFormat("ccc dd/MM") }`,
	};
}

/**
 * Formate la date d'une séance depuis son timestamp (secondes) — indépendant du format que
 * l'API sérialise selon le groupe. Garantit un affichage identique quelle que soit la source
 * (liste planning, patch depuis la page détail, cascade, etc.). Combiné à `capitalize` en CSS.
 *   short → "sam. 04/07", long → "samedi 4 juillet 2026"
 */
function formatSessionDate(timestamp: number | undefined | null, variant: "short" | "short-year" | "long" = "short"): string
{
	if (! timestamp) return "";
	const dt = DateTime.fromSeconds(Number(timestamp)).setLocale("fr");
	if (variant === "long") return dt.toFormat("cccc d LLLL yyyy");
	if (variant === "short-year") return dt.toFormat("ccc dd/MM/yyyy");
	return dt.toFormat("ccc dd/MM");
}

/** Formate une date ISO (chaîne) en français, 1re lettre capitalisée. « short » = « Ven. 10 juil. 2026 »,
 *  « long » = « Ven. 10 juillet 2026 ». Null/vide → « — ». (formatSessionDate prend un timestamp.) */
function formatIsoDate(iso: string | undefined | null, variant: "short" | "long" = "short"): string
{
	if (! iso) return "—";
	const fmt = variant === "long" ? "ccc dd LLLL yyyy" : "ccc d LLL yyyy";
	return DateTime.fromISO(iso).setLocale("fr").toFormat(fmt).replace(/^./, c => c.toUpperCase());
}

/** Mois et année d'une date ISO, 1re lettre capitalisée : « Septembre 2026 ». Null/invalide → chaîne vide. */
/**
 * « Aujourd'hui », « Hier », « Avant-hier », puis « Jeu. 20 sept. 2026 ».
 *
 * Les trois derniers jours se nomment, au-delà ils se datent : personne ne compte « il y a 4 jours ».
 */
function formatRelativeDayLabel(iso: string | undefined | null): string
{
	if (! iso) return "—";

	const days = DateTime.now().startOf("day").diff(DateTime.fromISO(iso).startOf("day"), "days").days;

	if (days === 0) return "Aujourd'hui";
	if (days === 1) return "Hier";
	if (days === 2) return "Avant-hier";

	return formatIsoDate(iso);
}

/** Heure d'une date ISO : « 14h05 ». Null/invalide → chaîne vide. */
function formatIsoTime(iso: string | undefined | null): string
{
	return parseToDateTime(iso as string)?.toFormat("HH'h'mm") ?? "";
}

function formatMonthLabel(iso: string | undefined | null): string
{
	const date = parseToDateTime(iso as string);

	return date?.isValid ? date.setLocale("fr").toFormat("LLLL yyyy").replace(/^./, c => c.toUpperCase()) : "";
}

/** Jour et mois d'une date ISO : « 20/09 ». Null/invalide → chaîne vide. */
function formatDayMonthLabel(iso: string | undefined | null): string
{
	const date = parseToDateTime(iso as string);

	return date?.isValid ? date.toFormat("dd/MM") : "";
}

/**
 * Sépare une date formatée par l'API de son heure : « Lun. 28/09 à 12h00 » donne
 * `{ date: "Lun. 28/09", time: "12h00" }`. De quoi les afficher sur deux lignes.
 *
 * `time` vaut `undefined` quand le format ne porte pas d'heure.
 */
function splitDateAndTime(value: string | undefined | null): { date: string, time: string | undefined }
{
	const [ date, time ] = (value ?? "").split(" à ");

	return { date, time };
}

export {
	MONTHS,
	MONTHS_SHORT,
	getShortFutureRelativeDate,
	splitDateAndTime,
	isNow,
	delay,
	parseToDateTime,
	calculateAge,
	getWeekLabels,
	formatSessionDate,
	formatIsoDate,
	formatIsoTime,
	formatRelativeDayLabel,
	formatMonthLabel,
	formatDayMonthLabel
};
